import { zodResolver } from '@hookform/resolvers/zod';
import { AnimatePresence, motion } from 'motion/react';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';

import {
  Badge,
  Button,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
  Textarea,
  FormControl as V4FormControl,
} from '@monksflow/monks-ui';

import {
  defaultFormValues,
  mapFormDataToDto,
  buildNacbIntakeFormSchema,
  useNACBIntakeMutation,
  type NACBIntakeFormData,
  type NACBIntakeFormInput,
} from '@/features/nacb-intake';
import { useTranslation } from 'react-i18next';

// Each option is a `z.enum` member and the value POSTed to the intake endpoint, so the
// strings may not change; the label maps below carry the copy.
/* eslint-disable i18next/no-literal-string -- wire values, not copy */
const BRAND_OPTIONS = [
  'OFF!',
  'RAID',
  'STEM',
  'Glade',
  'Ziploc',
  'Pledge',
  'Scrubbing Bubbles',
  'Windex',
  'Shout',
  'Drano',
  'Other',
] as const;

const TURNAROUND_OPTIONS = [
  'Fast (1–2 day turnaround)',
  'Opportunistic (~1 week)',
  'Not urgent (1–2 months)',
] as const;

const INVOLVEMENT_OPTIONS = ['High', 'Medium', 'Low'] as const;
/* eslint-enable i18next/no-literal-string */

// Only `Other` is copy; the rest are brand names, which read the same in every locale.
const BRAND_LABEL_KEY = {
  Other: 'nacb.nacbintakeFormPage.other',
} as const satisfies Partial<Record<(typeof BRAND_OPTIONS)[number], string>>;

const TURNAROUND_LABEL_KEY = {
  'Fast (1–2 day turnaround)': 'nacb.nacbintakeFormPage.fast12DayTurnaround',
  'Opportunistic (~1 week)': 'nacb.nacbintakeFormPage.opportunistic1Week',
  'Not urgent (1–2 months)': 'nacb.nacbintakeFormPage.notUrgent12Months',
} as const satisfies Record<(typeof TURNAROUND_OPTIONS)[number], string>;

const INVOLVEMENT_LABEL_KEY = {
  High: 'nacb.nacbintakeFormPage.high',
  Medium: 'nacb.nacbintakeFormPage.medium',
  Low: 'nacb.nacbintakeFormPage.low',
} as const satisfies Record<(typeof INVOLVEMENT_OPTIONS)[number], string>;

export function NACBIntakeFormPage() {
  const { t } = useTranslation('springboard');
  const { t: tValidation } = useTranslation('validation');

  const navigate = useNavigate();
  const { submit, isSubmitting } = useNACBIntakeMutation();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const formSchema = useMemo(() => buildNacbIntakeFormSchema(tValidation), [tValidation]);

  const form = useForm<NACBIntakeFormInput, undefined, NACBIntakeFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: defaultFormValues,
    mode: 'onTouched',
  });

  const { control, handleSubmit, reset } = form;

  const onSubmit = useCallback(
    async (data: NACBIntakeFormData) => {
      setSubmitError(null);
      // useNACBIntakeMutation will notify the user of success or failure using notification service.
      try {
        const dto = mapFormDataToDto(data);
        await submit(dto);
        reset(defaultFormValues);
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : t('nacb.nacbintakeFormPage.anUnexpectedErrorOccurred');
        setSubmitError(message);
      }
    },
    [submit, reset, t],
  );

  return (
    <div data-testid="nacb-intake-form-layout" className="h-full overflow-y-auto">
      <motion.div
        className="flex flex-col gap-4 p-4"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.3 }}
      >
        <div className="flex flex-col rounded-lg bg-card max-w-160 mx-auto my-6">
          <div className="border-b border-border py-6 px-6 shrink-0">
            <h1 className="text-3xl my-4 text-center">
              {t('nacb.nacbintakeFormPage.nacbTriggerIntakeForm')}
            </h1>
            <p className="text-lg mb-2">{t('nacb.nacbintakeFormPage.useThisFormToTrigger')}</p>
            <p className="text-lg mb-2">{t('nacb.nacbintakeFormPage.whenYouSubmitThisForm')}</p>
            <p className="text-sm text-foreground">
              <span aria-hidden="true" className="text-destructive">
                *
              </span>
              {t('nacb.nacbintakeFormPage.required')}
            </p>
          </div>
          <div className="py-6 px-6">
            <Form {...form}>
              <form onSubmit={(e) => void handleSubmit(onSubmit)(e)}>
                <div className="flex flex-col gap-8">
                  <FormField
                    control={control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {t('nacb.nacbintakeFormPage.name')}
                          <span aria-hidden="true" className="text-destructive">
                            *
                          </span>
                        </FormLabel>
                        <V4FormControl>
                          <Input
                            {...field}
                            placeholder={t('nacb.nacbintakeFormPage.enterYourName')}
                            disabled={isSubmitting}
                            className="bg-background"
                          />
                        </V4FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={control}
                    name="brand"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {t('nacb.nacbintakeFormPage.brand')}
                          <span aria-hidden="true" className="text-destructive">
                            *
                          </span>
                        </FormLabel>
                        <Select
                          value={field.value}
                          onValueChange={field.onChange}
                          disabled={isSubmitting}
                        >
                          <V4FormControl>
                            <SelectTrigger>
                              <SelectValue
                                placeholder={t('nacb.nacbintakeFormPage.selectABrand')}
                              />
                            </SelectTrigger>
                          </V4FormControl>
                          <SelectContent className="pointer-events-auto">
                            {BRAND_OPTIONS.map((option) => (
                              <SelectItem key={option} value={option}>
                                {option === 'Other' ? t(BRAND_LABEL_KEY.Other) : option}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={control}
                    name="businessSituation"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="leading-[1.3]">
                          {t('nacb.nacbintakeFormPage.whatBusinessSituationSocialOr')}
                          <span aria-hidden="true" className="text-destructive">
                            *
                          </span>
                        </FormLabel>
                        <V4FormControl>
                          <Textarea
                            {...field}
                            placeholder={t('nacb.nacbintakeFormPage.pleaseEnterAtMost400')}
                            rows={3}
                            disabled={isSubmitting}
                          />
                        </V4FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={control}
                    name="turnaroundTimeframe"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {t('nacb.nacbintakeFormPage.pleaseSelectTheDesiredTurnaround')}
                          <span aria-hidden="true" className="text-destructive">
                            *
                          </span>
                        </FormLabel>
                        <V4FormControl>
                          <RadioGroup
                            value={field.value}
                            onValueChange={field.onChange}
                            disabled={isSubmitting}
                            className="gap-3"
                          >
                            {TURNAROUND_OPTIONS.map((option, index) => {
                              const id = `turnaround-timeframe-${index}`;
                              return (
                                <div key={option} className="flex items-start gap-2">
                                  <RadioGroupItem value={option} id={id} />
                                  <Label htmlFor={id} className="cursor-pointer">
                                    {t(TURNAROUND_LABEL_KEY[option])}
                                  </Label>
                                </div>
                              );
                            })}
                          </RadioGroup>
                        </V4FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={control}
                    name="involvementLevel"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {t('nacb.nacbintakeFormPage.howWouldYouClassifyThe')}
                          <span aria-hidden="true" className="text-destructive">
                            *
                          </span>
                        </FormLabel>
                        <V4FormControl>
                          <RadioGroup
                            value={field.value}
                            onValueChange={field.onChange}
                            disabled={isSubmitting}
                            className="gap-3"
                          >
                            {INVOLVEMENT_OPTIONS.map((option, index) => {
                              const id = `involvement-level-${index}`;
                              return (
                                <div key={option} className="flex items-start gap-2">
                                  <RadioGroupItem value={option} id={id} />
                                  <Label htmlFor={id} className="cursor-pointer">
                                    {t(INVOLVEMENT_LABEL_KEY[option])}
                                  </Label>
                                </div>
                              );
                            })}
                          </RadioGroup>
                        </V4FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <AnimatePresence>
                    {submitError && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.3 }}
                      >
                        <Badge variant="destructive" className="w-full">
                          {submitError}
                        </Badge>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <div className="flex flex-row justify-center gap-2 pt-4">
                    <Button type="submit" disabled={isSubmitting}>
                      {isSubmitting ? (
                        <>
                          {t('nacb.nacbintakeFormPage.submitting')} <Spinner aria-hidden />
                        </>
                      ) : (
                        t('nacb.nacbintakeFormPage.submitRequest')
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={isSubmitting}
                      onClick={() => void navigate('/')}
                    >
                      {t('nacb.nacbintakeFormPage.cancel')}
                    </Button>
                  </div>
                </div>
              </form>
            </Form>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
